import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarOutlined, RollbackOutlined, SearchOutlined } from "@ant-design/icons";
import { Popover } from "antd";
import { FaPlane } from "react-icons/fa";
import { HiCheckCircle } from "react-icons/hi2";
import { useAppContext } from "../../../contexts/AppContext";
import { isAdminUser } from "../../../services/authService";
import { canShowAdminPriceTrace } from "../../../features/admin-price-trace/adminPriceTraceFlags";
import {
  getFlightBaggageIncludedBadge,
  resolveFlightBaggageDisplayState,
} from "../adapters/flightBaggageStepPolicy";
import { buildIncludedBaggageLines } from "../../../features/unifiedFlightSearch/duffelExtras";
import { mapRawLdsSupplementsToMockupExtras } from "../adapters/flightOfferAdapter";
import { findAirlineLogoFromAdmin } from "../../../utils/airlineLogoFromAdmin";
import { formatEndpointFromCodeAndName } from "../../../utils/flightEndpointDisplayHe";
import { formatFlightNumberWithAirlineCode } from "../../Common/UnifiedPackageFlightLeg/packageFlightLegAdapter";
import {
  airlineDisplayNameFromCode,
  buildAirlineNameMap,
} from "../../../utils/airlineNameFromCode";
import FlightResultCard from "../../FlightResultCard/FlightResultCard";
import { resolveAgentFlightDisplayedSortPrice } from "./resolveAgentFlightDisplayedSortPrice";
import { formatFlightReviewDisplayMoney } from "./resolveFlightReviewDisplayMoney";
import "./FlightOfferResultCard.css";

function legEndpointDisplayLine(leg, side) {
  if (!leg) return "—";
  if (side === "dep") {
    if (leg.originLocationLineHe) return leg.originLocationLineHe;
    const code = leg.originCode;
    const c = code != null && String(code).trim() !== "" && String(code).trim() !== "—" ? String(code).trim() : "";
    return formatEndpointFromCodeAndName(c, leg.originCityLabel || c);
  }
  if (leg.destinationLocationLineHe) return leg.destinationLocationLineHe;
  const code = leg.destinationCode;
  const c = code != null && String(code).trim() !== "" && String(code).trim() !== "—" ? String(code).trim() : "";
  return formatEndpointFromCodeAndName(c, leg.destinationCityLabel || c);
}

const kiwiAirlineLogoUrl = (code, px = 64) =>
  `https://images.kiwi.com/airlines/${px}/${encodeURIComponent(code)}.png`;

/** Same asset pattern as public flights card (visual only, not business rules). */
const publicStyleAvsLogoUrl = (code) =>
  `https://pics.avs.io/100/100/${encodeURIComponent(code)}.png`;

function buildPublicStyleServiceTag(rows, offer) {
  const allDirect = rows.length > 0 && rows.every((row) => row?.isDirect !== false);
  const firstCategory = rows.find((row) => row?.flightCategory)?.flightCategory;
  const text = String(firstCategory || offer?.ticketType || "").trim();
  if (/charter|צ'רטר|שכר/i.test(text)) return "צ'רטר";
  return allDirect ? "טיסה ישירה" : "קונקשן";
}

/** Unified multi-provider search — visible supplier ticker on agent result cards. */
function flightProviderTickerLabel(offer) {
  const p = String(offer?.provider || "").trim().toLowerCase();
  if (p === "duffel") return "DUFFEL";
  if (p === "lds") return "LDS";
  return null;
}

/** כמו `durationLabel` ב־PublicFlightResultWrapper — תצוגה בלבד. */
function durationLabelPublicCard(duration) {
  const value = String(duration || "").trim();
  if (!value || value === "—") return "משך לא צוין";
  return /ש|ד/u.test(value) ? value : `${value} שעות`;
}

function formatUsdTraceAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n).toFixed(2);
  return n < 0 ? `-$${abs}` : `$${abs}`;
}

function formatIlsTraceAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `₪${n.toFixed(2)}`;
}

function formatDeltaSourceLabel(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "unknown";
  const map = {
    system_margin: "System Margin",
    supplier_commission: "Supplier Commission",
    credit_card: "Credit Card Fee",
    agent_markup: "Agent Markup",
    rounding: "Rounding",
  };
  return map[raw] || raw;
}

function parseDurationHmToMinutes(durationStr) {
  if (!durationStr || typeof durationStr !== "string") return null;
  const m = durationStr.trim().match(/^(\d+):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
  return h * 60 + min;
}

/** תצוגה עברית למשך קטע בודד (מחרוזת H:MM כמו מה-adapter) */
function formatDurationHmToHe(durationStr) {
  if (!durationStr || durationStr === "—") return null;
  const mins = parseDurationHmToMinutes(durationStr);
  if (mins == null || mins <= 0) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}ש׳ ${m}ד׳`;
}

function v2DurationText(duration) {
  const raw = String(duration || "").trim();
  if (/^\d+:\d{2}$/.test(raw)) return raw;
  const he = formatDurationHmToHe(duration);
  if (he) return he;
  return durationLabelPublicCard(duration);
}

function compactExistingBaggageLine(line) {
  return String(line || "")
    .replace(/\s*לנוסע לכיוון/gu, "")
    .replace(/\bKG\b/gi, "ק״ג")
    .replace(/ק"ג/g, "ק״ג")
    .replace(/כבודת יד\s*\/\s*טרולי/gu, "טרולי")
    .replace(/יחידות כבודת יד/gu, "טרולי")
    .replace(/מזוודה רשומה/gu, "מזוודה")
    .replace(/מזוודות רשומות/gu, "מזוודות")
    .replace(/\s+/g, " ")
    .trim();
}

/** Presentation only. Prefer Wave 1A ViewModel chips when present (no rawDeal). */
function v2BaggageChipLabels(offer) {
  if (Array.isArray(offer?.fe2BaggageChips) && offer.fe2BaggageChips.length > 0) {
    return offer.fe2BaggageChips;
  }
  const resolved = resolveFlightBaggageDisplayState(offer?.rawDeal ?? null);
  if (resolved.displayState === "included" && resolved.includedLines.length > 0) {
    const chips = resolved.includedLines
      .map(compactExistingBaggageLine)
      .filter(Boolean);
    if (chips.length === 1 && chips[0] === "טרולי") return ["טרולי בלבד"];
    return chips;
  }
  const unifiedLines = buildIncludedBaggageLines(offer?.baggage);
  if (unifiedLines.length > 0) {
    const chips = unifiedLines.map(compactExistingBaggageLine).filter(Boolean);
    if (chips.length === 1 && chips[0] === "טרולי") return ["טרולי בלבד"];
    return chips;
  }
  if (resolved.displayState === "paid_upgrade") {
    return ["כבודה בתשלום"];
  }
  // Absence of included/purchase data is unknown — never "ללא כבודה"
  return ["פרטי כבודה בפירוט"];
}

function v2BaggageChipTone(label) {
  const t = String(label || "");
  if (/בתשלום/u.test(t)) return "paid";
  if (/ללא מזוודה|ללא כבודה|בפירוט/u.test(t)) return "muted";
  if (/טרולי|מזוודה|כלול/u.test(t)) return "positive";
  return "neutral";
}

function v2StopLabel(leg) {
  if (leg?.isDirect !== false) return "ישיר";
  const raw = String(leg?.stopLabelHe || "").trim();
  if (/אחת|1\b/.test(raw)) return "עצירה 1";
  return raw || "עצירות";
}

function baggageIncludedToken(deal) {
  const rows = mapRawLdsSupplementsToMockupExtras(deal);
  const trolleys = rows.filter((r) => r.type === "TROLLEY");
  if (trolleys.length === 0) return null;
  const allFree = trolleys.every((r) => Number(r.fareAmount) === 0);
  if (allFree) return "טרולי כלול";
  const someFree = trolleys.some((r) => Number(r.fareAmount) === 0);
  if (someFree) return "טרולי כלול";
  return "טרולי בתוספת";
}

function buildTripSummaryLine(legRows, deal) {
  if (!legRows.length) return null;
  const allDirect = legRows.every((l) => l?.isDirect !== false);
  const directPart = allDirect ? "ישיר" : "עם עצירות";
  const bagPart = baggageIncludedToken(deal);
  // לא מציגים כאן סכום משכי הלוך+חזור — המשך מוצג ליד כל כיוון בנפרד
  const parts = [directPart, bagPart].filter(Boolean);
  return parts.length ? parts.join(" • ") : null;
}

/** variant leg / legPremium = לוגו לכל קטע הלוך/חזור; legPremium = חבילות נופש / פרטים מלאים */
function AirlineLogo({ airlineCode, variant = "default" }) {
  const { airlines } = useAppContext();
  const isLegPremium = variant === "legPremium";
  const isLeg = variant === "leg" || isLegPremium;
  const adminLogo = useMemo(
    () => findAirlineLogoFromAdmin(airlines, airlineCode),
    [airlines, airlineCode]
  );

  const code =
    airlineCode != null && String(airlineCode).trim() !== ""
      ? String(airlineCode).trim().toUpperCase()
      : null;

  const [preferKiwi, setPreferKiwi] = useState(() => !adminLogo);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
    setPreferKiwi(!adminLogo);
  }, [adminLogo, code]);

  const onImgError = useCallback(() => {
    if (adminLogo && !preferKiwi) {
      setPreferKiwi(true);
      return;
    }
    setFailed(true);
  }, [adminLogo, preferKiwi]);

  const showAdmin = Boolean(adminLogo && !preferKiwi);
  const kiwiPx = isLegPremium ? 128 : isLeg ? 96 : 64;
  const src = showAdmin
    ? adminLogo
    : code
      ? kiwiAirlineLogoUrl(code, kiwiPx)
      : null;

  const wrapClass = [
    "fpc__logo-wrap",
    "fpc__logo-wrap--bare",
    isLeg ? "fpc__logo-wrap--leg" : "",
    isLegPremium ? "fpc__logo-wrap--leg-premium" : "",
    !src || failed ? "fpc__logo-wrap--fallback" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const imgDisplay = isLegPremium ? 96 : isLeg ? 56 : 52;

  if (!src || failed) {
    return (
      <div className={wrapClass} aria-hidden>
        <FaPlane
          className={
            isLegPremium
              ? "fpc__logo-fallback fpc__logo-fallback--leg fpc__logo-fallback--leg-premium"
              : isLeg
                ? "fpc__logo-fallback fpc__logo-fallback--leg"
                : "fpc__logo-fallback"
          }
          aria-hidden
        />
      </div>
    );
  }

  return (
    <div className={wrapClass}>
      <img
        src={src}
        alt=""
        width={imgDisplay}
        height={imgDisplay}
        className={
          isLegPremium
            ? "fpc__logo-img fpc__logo-img--leg fpc__logo-img--leg-premium"
            : isLeg
              ? "fpc__logo-img fpc__logo-img--leg"
              : "fpc__logo-img"
        }
        loading="lazy"
        decoding="async"
        onError={onImgError}
      />
    </div>
  );
}

export function FlightLegBlock({
  leg,
  showDivider,
  showSupplierInMeta,
  legIndex = 0,
  airlineNameMap = {},
  /** תוכן נוסף (מטען, מקומות, הערות ספק) — אחרי שורת המפעיל */
  footerSlot = null,
  /** תצוגת mini-site / פרטי חבילה — היררכיה, timeline ופוטר מסודרים */
  packagePremiumLayout = false,
}) {
  const originLine = legEndpointDisplayLine(leg, "dep");
  const destLine = legEndpointDisplayLine(leg, "arr");
  const dep = leg?.departureTime || "—";
  const arr = leg?.arrivalTime || "—";
  const isDirect = leg?.isDirect !== false;
  const stopLabel = isDirect
    ? "טיסה ישירה"
    : leg?.stopLabelHe || "עם עצירות";
  const airlineCode =
    leg?.airlineCode != null && String(leg.airlineCode).trim() !== ""
      ? String(leg.airlineCode).trim().toUpperCase()
      : null;
  const airlineDisplayName = airlineDisplayNameFromCode(airlineCode, airlineNameMap);
  const flightNumberRaw =
    leg?.flightNumber != null && String(leg.flightNumber).trim() !== ""
      ? String(leg.flightNumber).trim()
      : null;
  const flightNumber = formatFlightNumberWithAirlineCode(airlineCode, flightNumberRaw);
  const cabinClass =
    leg?.flightCategory != null && String(leg.flightCategory).trim() !== ""
      ? String(leg.flightCategory).trim()
      : "";
  const directLabelForSummary = isDirect ? "טיסה ישירה" : stopLabel;
  const nextDayLandingLabel =
    packagePremiumLayout && leg?.secondaryArrDateLabel
      ? String(leg.secondaryArrDateLabel).replace(/^נחיתה\s+/u, "נחיתה ביום הבא ")
      : leg?.secondaryArrDateLabel;
  const durationHe = formatDurationHmToHe(leg?.duration);
  const showDurationOnTimeline = Boolean(packagePremiumLayout && durationHe);
  const isReturnLeg = /חזור|return/i.test(leg?.labelHe || "");

  const metaParts = [];
  if (showSupplierInMeta && leg?.supplierCode) {
    metaParts.push(`ספק ${leg.supplierCode}`);
  }

  const legHeadingId = `fpc-leg-${leg?.id ?? legIndex}`;

  return (
    <>
      {showDivider ? <div className="fpc__leg-divider" aria-hidden /> : null}
      <section
        className={`fpc__leg${packagePremiumLayout ? " fpc__leg--package-premium" : ""}`}
        aria-labelledby={legHeadingId}
      >
        <header
          className={`fpc__leg-header${packagePremiumLayout ? " fpc__leg-header--premium" : ""}`}
        >
          {packagePremiumLayout ? (
            <div className="fpc__leg-header-premium-start">
              <span
                className={`fpc__leg-dir-pill${isReturnLeg ? " fpc__leg-dir-pill--return" : ""}`}
                id={legHeadingId}
              >
                {isReturnLeg ? (
                  <RollbackOutlined className="fpc__leg-dir-icon" aria-hidden />
                ) : (
                  <FaPlane className="fpc__leg-dir-icon fpc__leg-dir-icon--out" aria-hidden />
                )}
                <span className="fpc__leg-dir-text">{leg?.labelHe ?? "טיסה הלוך"}</span>
              </span>
            </div>
          ) : (
            <span className="fpc__leg-label" id={legHeadingId}>
              {leg?.labelHe ?? "הלוך"}
            </span>
          )}
          {leg?.depDateDisplay ? (
            <time
              className={`fpc__leg-date${packagePremiumLayout ? " fpc__leg-date--premium" : ""}`}
              dateTime={leg?.depDate ?? undefined}
            >
              <span className="fpc__leg-date-prefix">תאריך </span>
              {leg.depDateDisplay}
            </time>
          ) : null}
        </header>

        {/* סריקה ראשונה: זמנים ומסלול */}
        <div
          className={`fpc__leg-path fpc__leg-path--primary${packagePremiumLayout ? " fpc__leg-path--premium" : ""}`}
          dir="rtl"
          role="group"
          aria-label={`${leg?.labelHe ?? "כיוון"}: ${originLine} ל-${destLine}`}
        >
          <div className="fpc__leg-end fpc__leg-end--dep">
            <time
              className="fpc__leg-time"
              dateTime={leg?.depDate ?? undefined}
            >
              {dep}
            </time>
            <span className="fpc__leg-kind">המראה</span>
            <span className="fpc__leg-location" dir="rtl">
              {originLine}
            </span>
          </div>
          <div
            className={`fpc__leg-track${packagePremiumLayout ? " fpc__leg-track--premium" : ""}`}
            aria-hidden
          >
            {showDurationOnTimeline && !packagePremiumLayout ? (
              <span className="fpc__duration-on-track">משך {durationHe}</span>
            ) : null}
            <div className="fpc__track-inner">
              <span className="fpc__path-node" />
              <span className="fpc__path-line" />
              <FaPlane className="fpc__path-plane" />
              <span className="fpc__path-line" />
              <span className="fpc__path-node" />
            </div>
            {packagePremiumLayout ? (
              <div className="fpc__track-meta" role="presentation">
                {durationHe ? <span className="fpc__track-meta-chip">משך {durationHe}</span> : null}
                <span className="fpc__track-meta-chip">{directLabelForSummary}</span>
              </div>
            ) : null}
          </div>
          <div className="fpc__leg-end fpc__leg-end--arr">
            <time
              className="fpc__leg-time"
              dateTime={leg?.arrDate ?? undefined}
            >
              {arr}
            </time>
            <span className="fpc__leg-kind">נחיתה</span>
            <span className="fpc__leg-location" dir="rtl">
              {destLine}
            </span>
            {nextDayLandingLabel ? (
              packagePremiumLayout ? (
                <div className="fpc__leg-nextday" role="status" dir="rtl">
                  <CalendarOutlined className="fpc__leg-nextday-icon" aria-hidden />
                  <span className="fpc__leg-nextday-text">{nextDayLandingLabel}</span>
                </div>
              ) : (
                <span className="fpc__leg-subdate" dir="rtl">
                  {nextDayLandingLabel}
                </span>
              )
            ) : null}
          </div>
        </div>

        {/* עובדות מספריות: עצירות · משך · מספר טיסה */}
        <div
          className={`fpc__leg-facts${packagePremiumLayout ? " fpc__leg-facts--premium" : ""}`}
          role="group"
        >
          {packagePremiumLayout ? (
            <>
              {flightNumber ? (
                <span className="fpc__leg-fact fpc__leg-fact--flight" dir="ltr">
                  {flightNumber}
                </span>
              ) : null}
              {flightNumber ? (
                <span className="fpc__leg-fact-sep" aria-hidden>
                  ·
                </span>
              ) : null}
              <span
                className={`fpc__leg-fact fpc__leg-fact--stops${isDirect ? " fpc__leg-fact--direct" : ""}`}
              >
                {directLabelForSummary}
              </span>
            </>
          ) : (
            <>
              <span
                className={`fpc__leg-fact fpc__leg-fact--stops${isDirect ? " fpc__leg-fact--direct" : ""}`}
              >
                {stopLabel}
              </span>
              {durationHe && !showDurationOnTimeline ? (
                <>
                  <span className="fpc__leg-fact-sep" aria-hidden>
                    ·
                  </span>
                  <span className="fpc__leg-fact">משך {durationHe}</span>
                </>
              ) : null}
              {flightNumber ? (
                <>
                  <span className="fpc__leg-fact-sep" aria-hidden>
                    ·
                  </span>
                  <span className="fpc__leg-fact fpc__leg-fact--flight" dir="ltr">
                    {flightNumber}
                  </span>
                </>
              ) : null}
            </>
          )}
        </div>

        {/* מטא מסחרי משני: חברה ומחלקה */}
        <div
          className={`fpc__leg-carrier${packagePremiumLayout ? " fpc__leg-carrier--premium" : ""}`}
        >
          <AirlineLogo
            airlineCode={leg?.airlineCode}
            variant={packagePremiumLayout ? "legPremium" : "leg"}
          />
          <div className="fpc__leg-carrier-info">
            {!packagePremiumLayout ? (
              <span className="fpc__leg-carrier-line">
                <span
                  className={
                    airlineDisplayName === "—"
                      ? "fpc__leg-airline-name fpc__leg-airline-name--muted"
                      : "fpc__leg-airline-name"
                  }
                  dir="auto"
                >
                  {airlineDisplayName}
                </span>
              </span>
            ) : null}
            {cabinClass && !packagePremiumLayout ? (
              <span className="fpc__leg-cabin">
                <span className="fpc__leg-cabin-label">מחלקה</span>
                <span className="fpc__leg-cabin-value" dir="ltr">
                  {cabinClass}
                </span>
              </span>
            ) : null}
          </div>
        </div>

        {footerSlot ? (
          <div
            className={`fpc__leg-footer-slot${packagePremiumLayout ? " fpc__leg-footer-slot--premium" : ""}`}
          >
            {footerSlot}
          </div>
        ) : null}

        {metaParts.length > 0 ? (
          <p className="fpc__leg-meta">{metaParts.join(" · ")}</p>
        ) : null}
      </section>
    </>
  );
}

function buildLegRowsFallback(offer) {
  const rows = offer?.cardLegRows;
  if (Array.isArray(rows) && rows.length > 0) return rows;
  const m = offer?.cardMeta;
  if (!m) return [];
  const lc = Number(m.legCount) || 0;
  let stopLabelHe = "ישירה";
  if (lc === 2) stopLabelHe = "עצירה אחת";
  else if (lc > 2) stopLabelHe = `${lc - 1} עצירות`;
  return [
    {
      id: "meta-fallback",
      labelHe: "הלוך",
      departureTime: m.departureTime,
      arrivalTime: m.arrivalTime,
      originCode: m.originCode,
      destinationCode: m.destinationCode,
      depDate: m.depDate,
      arrDate: m.arrDate,
      depDateDisplay: m.depDateDisplay,
      duration: m.duration,
      isDirect: m.isDirect,
      stopLabelHe,
      flightCategory: m.flightCategory,
      airlineCode: m.airlineCode,
      flightNumber: m.flightNumber,
      supplierCode: m.supplierCode,
    },
  ];
}

/**
 * כרטיס תוצאת טיסה בסוכן — מוצג בתוך `FlightResultCard` עם אותו גריד 3 עמודות כמו באתר הפומבי
 * (לוגו + תג | פרטי טיסה | מחיר + פעולות). אין שינוי ללוגיקת מחירים/הזמנה.
 */
export default function FlightOfferResultCardLayout({
  offer,
  priceColumn,
  disabled = false,
  isCheapest = false,
  /** מופיע פעם אחת בלבד: הכי זול | הכי משתלם (דורס isCheapest לטקסט אם מועבר) */
  prominenceBadge = null,
  /** roundTrip: המשך להזמנה (תצוגה) | selfCompose: כפתור בחירת כיוון */
  ctaMode = "roundTrip",
  onPrimaryCtaClick,
  hidePrimaryCta = false,
  /** כיתוב CTA במצב selfCompose (למשל «בחר הלוך») */
  primaryCtaLabel = null,
  selected = false,
  showSupplierInMetaOverride,
  airlinesCatalog,
  appearance = "v1",
}) {
  const showSupplierInMeta =
    showSupplierInMetaOverride !== undefined
      ? showSupplierInMetaOverride
      : isAdminUser();
  const { airlines } = useAppContext();

  const legRows = useMemo(() => buildLegRowsFallback(offer), [offer]);
  const airlineNameMap = useMemo(
    () => buildAirlineNameMap(airlinesCatalog || airlines),
    [airlines, airlinesCatalog]
  );
  const primaryAirlineCode = useMemo(
    () => legRows.find((row) => row?.airlineCode)?.airlineCode || offer?.airlineCode,
    [legRows, offer]
  );
  const airlineName = useMemo(
    () =>
      primaryAirlineCode
        ? airlineDisplayNameFromCode(primaryAirlineCode, airlineNameMap)
        : offer?.airlineSummary || "חברת תעופה",
    [primaryAirlineCode, airlineNameMap, offer?.airlineSummary]
  );

  const agentAirlineStripContent = useMemo(() => {
    const brands = [];
    const seen = new Set();
    const sourceLegs =
      appearance === "v2" && legRows.length > 0 ? legRows : [null];
    for (const leg of sourceLegs) {
      const codeRaw = leg?.airlineCode || primaryAirlineCode;
      const code =
        codeRaw != null && String(codeRaw).trim() !== ""
          ? String(codeRaw).trim().toUpperCase()
          : "";
      const key = code || airlineName;
      if (seen.has(key)) continue;
      seen.add(key);
      const name = code
        ? airlineDisplayNameFromCode(code, airlineNameMap)
        : airlineName;
      const adminLogo = findAirlineLogoFromAdmin(airlines, code);
      const src = adminLogo || (code ? publicStyleAvsLogoUrl(code) : null);
      brands.push({ code, name, src });
    }
    if (brands.length === 0) {
      brands.push({ code: "", name: airlineName, src: null });
    }
    const serviceTagLabel = buildPublicStyleServiceTag(legRows, offer);
    const providerTicker =
      appearance === "v2" ? null : flightProviderTickerLabel(offer);
    const providerKey = String(offer?.provider || "").trim().toLowerCase();

    if (appearance !== "v2") {
      const brand = brands[0];
      const logo =
        !brand.src ? (
          <span className="flight-result-card__logo flight-result-card__logo--fallback" aria-hidden>
            <FaPlane />
          </span>
        ) : (
          <img
            src={brand.src}
            alt=""
            className="flight-result-card__logo"
            loading="lazy"
            decoding="async"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        );
      return (
        <>
          {logo}
          <strong className="flight-result-card__airline-name">{brand.name}</strong>
          <span className="flight-result-card__service-tag">{serviceTagLabel}</span>
          {providerTicker ? (
            <span
              className={`fpc__provider-ticker fpc__provider-ticker--${providerKey}`}
              title="ספק תוצאה"
            >
              {providerTicker}
            </span>
          ) : null}
        </>
      );
    }

    return (
      <>
        {brands.map((brand) => (
          <span className="flight-result-card__airline-brand" key={brand.code || brand.name}>
            {brand.src ? (
              <img
                src={brand.src}
                alt=""
                className="flight-result-card__logo"
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <span className="flight-result-card__logo flight-result-card__logo--fallback" aria-hidden>
                <FaPlane />
              </span>
            )}
            <strong className="flight-result-card__airline-name">{brand.name}</strong>
          </span>
        ))}
      </>
    );
  }, [
    airlineName,
    airlineNameMap,
    airlines,
    appearance,
    legRows,
    offer,
    primaryAirlineCode,
  ]);

  const publicStyleDetailsInner = useMemo(() => {
    if (!legRows.length) {
      return <p className="fpc__leg-empty">אין פרטי טיסה להצגה</p>;
    }
    const sameAirlineBothLegs =
      appearance === "v2" &&
      legRows.length > 1 &&
      legRows.every((leg) => {
        const code = String(leg?.airlineCode || primaryAirlineCode || "")
          .trim()
          .toUpperCase();
        const primary = String(primaryAirlineCode || "")
          .trim()
          .toUpperCase();
        return code !== "" && primary !== "" && code === primary;
      });
    return legRows.map((leg, index) => {
      const airlineCode = leg?.airlineCode || primaryAirlineCode;
      const rowAirline = airlineCode
        ? airlineDisplayNameFromCode(airlineCode, airlineNameMap)
        : airlineName;
      const isDirect = leg?.isDirect !== false;
      const stopText = appearance === "v2" ? v2StopLabel(leg) : (
        isDirect
          ? "ישיר"
          : leg?.stopLabelHe || "עצירות"
      );
      if (appearance === "v2") {
        return (
          <article
            className="flight-result-card__leg"
            key={leg?.id || `${leg?.labelHe}-${index}`}
          >
            <div className="flight-result-card__route-row">
              <span className="flight-result-card__leg-dir">
                {leg?.labelHe || (index === 0 ? "הלוך" : "חזור")}
              </span>
              <div className="flight-result-card__endpoint">
                <time
                  className="flight-result-card__time"
                  dateTime={leg?.depDate || undefined}
                  dir="ltr"
                >
                  {leg?.departureTime || "—"}
                </time>
                <span className="flight-result-card__airport" translate="no" dir="ltr">
                  {leg?.originCode || "—"}
                </span>
              </div>
              <span className="flight-result-card__sep" aria-hidden>
                ──
              </span>
              <div className="flight-result-card__path flight-result-card__path--v2text">
                <span className="flight-result-card__duration">
                  {v2DurationText(leg?.duration)}
                </span>
                <span
                  className={
                    isDirect
                      ? "flight-result-card__stops flight-result-card__stops--direct"
                      : "flight-result-card__stops flight-result-card__stops--connect"
                  }
                >
                  {stopText}
                </span>
              </div>
              <span className="flight-result-card__sep" aria-hidden>
                ──
              </span>
              <div className="flight-result-card__endpoint">
                <time
                  className="flight-result-card__time"
                  dateTime={leg?.arrDate || undefined}
                  dir="ltr"
                >
                  {leg?.arrivalTime || "—"}
                </time>
                <span className="flight-result-card__airport" translate="no" dir="ltr">
                  {leg?.destinationCode || "—"}
                </span>
              </div>
            </div>
            <div className="flight-result-card__flight-meta">
              {leg?.depDateDisplay ? (
                <time
                  className="flight-result-card__date"
                  dateTime={leg?.depDate || undefined}
                >
                  {leg.depDateDisplay}
                </time>
              ) : null}
              {leg?.flightNumber ? (
                <span translate="no" dir="ltr">
                  {leg.flightNumber}
                </span>
              ) : null}
              {!sameAirlineBothLegs && rowAirline && rowAirline !== "—" ? (
                <span className="flight-result-card__leg-airline">{rowAirline}</span>
              ) : null}
            </div>
          </article>
        );
      }
      return (
        <article className="flight-result-card__leg" key={leg?.id || `${leg?.labelHe}-${index}`}>
          <div className="flight-result-card__leg-head">
            <span className="flight-result-card__leg-title">
              {leg?.labelHe || (index === 0 ? "טיסת הלוך" : "טיסת חזור")}
            </span>
            {leg?.depDateDisplay ? (
              <time className="flight-result-card__date" dateTime={leg?.depDate || undefined}>
                {leg.depDateDisplay}
              </time>
            ) : null}
          </div>

          <div className="flight-result-card__route-row">
            <div className="flight-result-card__endpoint">
              <time className="flight-result-card__time" dateTime={leg?.depDate || undefined}>
                {leg?.departureTime || "—"}
              </time>
              <span className="flight-result-card__airport" translate="no">
                {leg?.originCode || leg?.originLocationLineHe || "—"}
              </span>
            </div>

            <div className="flight-result-card__path" aria-hidden>
              <span className="flight-result-card__path-line" />
              <FaPlane className="flight-result-card__plane" />
              <span className="flight-result-card__path-line" />
              <span className="flight-result-card__duration">
                {durationLabelPublicCard(leg?.duration)}
              </span>
            </div>

            <div className="flight-result-card__endpoint">
              <time className="flight-result-card__time" dateTime={leg?.arrDate || undefined}>
                {leg?.arrivalTime || "—"}
              </time>
              <span className="flight-result-card__airport" translate="no">
                {leg?.destinationCode || leg?.destinationLocationLineHe || "—"}
              </span>
            </div>

            <div className="flight-result-card__flight-meta">
              <span>{rowAirline}</span>
              {leg?.flightNumber ? <span translate="no">{leg.flightNumber}</span> : null}
            </div>
          </div>
        </article>
      );
    });
  }, [airlineName, airlineNameMap, appearance, legRows, primaryAirlineCode]);

  const structuredPrice = true;

  const tripSummaryLine = useMemo(
    () => buildTripSummaryLine(legRows, offer?.rawDeal),
    [legRows, offer?.rawDeal]
  );

  const baggageIncludedBadge = useMemo(
    () => getFlightBaggageIncludedBadge(offer?.rawDeal ?? null),
    [offer?.rawDeal]
  );
  const v2BaggageChips = useMemo(
    () => (appearance === "v2" ? v2BaggageChipLabels(offer) : []),
    [appearance, offer]
  );
  const v2FareChips = useMemo(() => {
    if (appearance !== "v2") return [];
    const seen = new Set();
    const out = [];
    for (const value of [
      offer?.cabinClass,
      offer?.fareBrand,
      legRows[0]?.flightCategory,
    ]) {
      const t = String(value || "").trim();
      if (!t || seen.has(t)) continue;
      seen.add(t);
      out.push(t);
    }
    return out;
  }, [appearance, offer, legRows]);

  const resolvedBadge = prominenceBadge ?? (isCheapest ? "cheapest" : null);
  const displayedSortPrice = useMemo(
    () => resolveAgentFlightDisplayedSortPrice(offer),
    [offer]
  );
  const ilsDisplayAmount = displayedSortPrice.displayedIls;
  const usdDisplayAmount = displayedSortPrice.displayedUsd;
  const primaryPriceLabel = displayedSortPrice.primaryPriceLabel ?? null;
  const isPrimaryCurrencyIls = displayedSortPrice.isPrimaryCurrencyIls;
  const totalPriceLabel =
    appearance === "v2" &&
    displayedSortPrice.priceUnit === "per_person" &&
    displayedSortPrice.displayedTotalIls != null
      ? formatFlightReviewDisplayMoney(
          displayedSortPrice.displayedTotalIls,
          "ILS"
        )
      : null;
  const totalPaxCount = Math.max(1, Number(offer?.totalPaxCount) || 0);
  const totalCaption =
    appearance === "v2" && totalPriceLabel && totalPaxCount > 1
      ? `סה״כ ל-${totalPaxCount} נוסעים`
      : appearance === "v2" && totalPriceLabel
        ? "סה״כ"
        : null;
  const prominenceHe =
    resolvedBadge === "cheapest"
      ? "הכי זול"
      : resolvedBadge === "value"
        ? "הכי משתלם"
        : null;
  const priceTrace = offer?.priceTrace || null;

  const dealIdForTraceLog = useMemo(
    () =>
      offer?.rawDeal?.dealId ??
      offer?.rawDeal?.ID ??
      offer?.rawDeal?.id ??
      null,
    [offer?.rawDeal?.dealId, offer?.rawDeal?.ID, offer?.rawDeal?.id]
  );

  useEffect(() => {
    const dev =
      (typeof import.meta !== "undefined" && import.meta.env?.DEV === true) ||
      process.env.NODE_ENV === "development";
    if (!dev || !priceTrace) return;
    const offerId = offer?.id ?? null;
    console.log("[FLIGHT_CARD_PRICE_TRACE_RENDERED]", {
      dealId: dealIdForTraceLog,
      offerId,
      priceTrace,
      hrMarginSsotUiLine: priceTrace.hrMarginSsotUiLine ?? null,
      hrMarginTraceSourceFeatureKey: priceTrace.hrMarginTraceSourceFeatureKey ?? null,
      configuredSystemMarginPercent:
        priceTrace.configuredSystemMarginPercent ??
        priceTrace.configuredSystemMarginRate ??
        null,
      finalHrPercentUsed: priceTrace.finalHrPercentUsed ?? null,
      unifiedHrMarginPercent: priceTrace.unifiedHrMarginPercent ?? null,
      packageFeatureCommissionPercentage:
        priceTrace.packageFeatureCommissionPercentage ?? null,
    });
  }, [dealIdForTraceLog, offer?.id, priceTrace]);

  const priceTraceRows = useMemo(() => {
    if (!priceTrace) return [];
    const labels = {
      apiGrossUsdPerPerson: "API Gross",
      supplierCommissionAmountUsd: "Supplier Commission",
      netUsdAfterSupplierCommission: "Net After Supplier",
      configuredSystemMarginRate: "Configured System Margin Rate",
      systemMarginAmountUsd: "System Margin (HR Hidden Margin)",
      agentMarkupAmountUsd: "Agent Fee",
      creditCardFeeUsd: "Credit Card Fee",
      roundingDeltaUsd: "Rounding Delta",
      finalClientUsdPerPerson: "Agent Net Price",
      usdRateUsed: "USD Rate Used",
      ilsHelper: "ILS Helper",
      displayedUsdPerPerson: "Final Client Price",
      deltaFromApiGrossUsd: "Delta",
      deltaSource: "Delta Source",
    };
    const importantKeys = new Set([
      "apiGrossUsdPerPerson",
      "finalClientUsdPerPerson",
      "displayedUsdPerPerson",
      "deltaFromApiGrossUsd",
    ]);
    return [
      ["apiGrossUsdPerPerson", priceTrace.apiGrossUsdPerPerson],
      ["supplierCommissionAmountUsd", priceTrace.supplierCommissionAmountUsd],
      ["netUsdAfterSupplierCommission", priceTrace.netUsdAfterSupplierCommission],
      ["configuredSystemMarginRate", priceTrace.configuredSystemMarginRate],
      [
        "systemMarginAmountUsd",
        priceTrace.systemMarginAmountUsd,
      ],
      ["agentMarkupAmountUsd", priceTrace.agentMarkupAmountUsd],
      ["creditCardFeeUsd", priceTrace.creditCardFeeUsd],
      ["roundingDeltaUsd", priceTrace.roundingDeltaUsd],
      ["finalClientUsdPerPerson", priceTrace.finalClientUsdPerPerson],
      ["usdRateUsed", priceTrace.usdRateUsed],
      ["ilsHelper", priceTrace.ilsHelper],
      ["displayedUsdPerPerson", priceTrace.displayedUsdPerPerson],
      ["deltaFromApiGrossUsd", priceTrace.deltaFromApiGrossUsd],
      ["deltaSource", priceTrace.primaryDeltaSource],
    ].map(([key, value]) => ({
      key,
      label: labels[key] || key,
      value,
      important: importantKeys.has(key),
    }));
  }, [priceTrace]);
  const traceMissingReason =
    offer?.priceClientPerPersonUsd == null
      ? "Missing ourTotalFare.origCurrency.priceWithCommissionAndCreditCardFee"
      : null;
  const priceTraceContent = (
    <div className="fpc__trace-popover" dir="ltr">
      <div className="fpc__trace-ssot">
        {priceTrace?.hrMarginSsotUiLine || "SSOT: unknown — check pricing audit"}
      </div>
      {traceMissingReason ? (
        <div className="fpc__trace-missing">
          <div className="fpc__trace-missing-title">Final USD missing</div>
          <div className="fpc__trace-missing-reason">Reason: {traceMissingReason}</div>
        </div>
      ) : null}
      {priceTraceRows.map((row) => (
        <div
          className={`fpc__trace-row${row.important ? " fpc__trace-row--important" : ""}`}
          key={row.key}
        >
          <span className="fpc__trace-key">{row.label}</span>
          <span className="fpc__trace-val" translate="no">
            {row.key === "deltaSource"
              ? formatDeltaSourceLabel(row.value)
              : row.key === "configuredSystemMarginRate"
                ? `${Number.isFinite(Number(row.value)) ? Number(row.value).toFixed(2) : "0.00"}%`
                : row.key === "usdRateUsed"
                  ? `${Number.isFinite(Number(row.value)) ? Number(row.value).toFixed(4) : "—"}`
                  : row.key === "ilsHelper"
                    ? formatIlsTraceAmount(row.value)
                : formatUsdTraceAmount(row.value)}
          </span>
        </div>
      ))}
    </div>
  );

  const priceTraceControl =
    structuredPrice && priceTrace && canShowAdminPriceTrace() ? (
      <Popover
        placement="bottomRight"
        trigger="click"
        overlayClassName="fpc__trace-popover-overlay"
        content={priceTraceContent}
      >
        <button
          type="button"
          className="fpc__price-trace-btn"
          aria-label="הצג פירוט TRACE למחיר"
          onClick={(e) => e.stopPropagation()}
        >
          <SearchOutlined />
        </button>
      </Popover>
    ) : null;

  if (!structuredPrice) {
    return (
      <div
        className={`fpc${disabled ? " fpc--disabled" : ""}${isCheapest ? " fpc--cheapest" : ""}`}
        dir="rtl"
      >
        <div className="fpc__zones">
          <div className="fpc__zone fpc__zone--flights">
            {legRows.length === 0 ? (
              <p className="fpc__leg-empty">אין פרטי טיסה להצגה</p>
            ) : (
              legRows.map((leg, idx) => (
                <FlightLegBlock
                  key={leg.id ?? idx}
                  leg={leg}
                  legIndex={idx}
                  showDivider={idx > 0}
                  showSupplierInMeta={showSupplierInMeta}
                  airlineNameMap={airlineNameMap}
                />
              ))
            )}
          </div>
          <aside className="fpc__zone fpc__zone--price">
            <div className="fpc__commerce">
              <div className="fpc__price-block fpc__price-block--legacy">
                {priceColumn ?? (
                  <span className="fw-ui-result-price-stack">
                    <span className="fw-ui-result-price-main">
                      {offer?.pricePerPersonDisplayLabel ?? "—"}
                    </span>
                    <span className="fpc__price-per-taxes fpc__price-per-taxes--legacy">
                      לאדם • כולל מיסים
                    </span>
                  </span>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    );
  }

  const detailsContent = (
    <>
      {publicStyleDetailsInner}
      {appearance === "v2" || !baggageIncludedBadge ? null : (
        <div className="fpc__baggage-included-badge" role="status">
          <HiCheckCircle className="fpc__baggage-included-icon" aria-hidden />
          <span className="fpc__baggage-included-text">{baggageIncludedBadge.labelHe}</span>
        </div>
      )}
      {appearance === "v2" || !tripSummaryLine ? null : (
        <p className="fpc__itinerary-footnote">{tripSummaryLine}</p>
      )}
    </>
  );

  const actionsSlot = (
    <>
      {structuredPrice && priceColumn ? <div className="fpc__price-extra">{priceColumn}</div> : null}
      {!hidePrimaryCta ? (
        <div className="fpc__cta-block">
          {ctaMode === "selfCompose" ? (
            <button
              type="button"
              className="flight-result-card__cta"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                onPrimaryCtaClick?.(e);
              }}
            >
              {primaryCtaLabel || "בחר טיסה"}
            </button>
          ) : onPrimaryCtaClick ? (
            <button
              type="button"
              className="flight-result-card__cta"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                onPrimaryCtaClick?.(e);
              }}
            >
              {appearance === "v2" ? "בחר טיסה" : "המשך להזמנה"}
            </button>
          ) : (
            <span className="flight-result-card__cta flight-result-card__cta--surface">
              {appearance === "v2" ? "בחר טיסה" : "המשך להזמנה"}
            </span>
          )}
          {appearance === "v2" ? null : (
            <p className="fpc__trust-note">המחיר יעודכן סופית לפני התשלום</p>
          )}
        </div>
      ) : null}
    </>
  );

  return (
    <FlightResultCard
      mode="agent"
      appearance={appearance}
      className={`fpc${disabled ? " fpc--disabled" : ""}${isCheapest ? " fpc--cheapest" : ""}${selected ? " fpc--selected" : ""} fpc--structured-price`}
      isCheapest={!!isCheapest}
      selected={selected}
      prominenceLabel={prominenceHe}
      isPrimaryCurrencyIls={isPrimaryCurrencyIls}
      primaryPriceLabel={primaryPriceLabel}
      priceIls={ilsDisplayAmount}
      priceUsd={usdDisplayAmount}
      totalLabel={totalPriceLabel}
      totalCaption={totalCaption}
      detailsContent={detailsContent}
      extrasContent={
        appearance === "v2" ? (
          <div className="flight-result-card__baggage" role="status" dir="rtl">
            {v2BaggageChips.map((label) => (
              <span
                className={`flight-result-card__baggage-chip flight-result-card__baggage-chip--${v2BaggageChipTone(label)}`}
                key={label}
              >
                {label}
              </span>
            ))}
          </div>
        ) : null
      }
      headerBadges={
        appearance === "v2" && v2FareChips.length > 0 ? (
          <div className="flight-result-card__fare-badges">
            {v2FareChips.map((label) => (
              <span
                className="flight-result-card__baggage-chip flight-result-card__baggage-chip--fare"
                key={`fare-${label}`}
              >
                {label}
              </span>
            ))}
          </div>
        ) : null
      }
      airlineContent={agentAirlineStripContent}
      priceAccessory={priceTraceControl}
      actions={actionsSlot}
    />
  );
}

export function FlightOfferResultCardSkeleton() {
  return (
    <div className="fpc fpc--skeleton" dir="rtl" aria-hidden>
      <div className="fpc__zones">
        <div className="fpc__zone fpc__zone--flights">
          <div className="fpc__sk fpc__sk-route" />
          <div className="fpc__leg-head fpc__leg-head--skeleton">
            <div className="fpc__sk fpc__sk-leg-label" />
            <div className="fpc__sk fpc__sk-logo-leg" />
          </div>
          <div className="fpc__sk fpc__sk-path" />
          <div className="fpc__sk fpc__sk-meta" />
          <div className="fpc__leg-divider fpc__leg-divider--sk" />
          <div className="fpc__leg-head fpc__leg-head--skeleton">
            <div className="fpc__sk fpc__sk-leg-label" />
            <div className="fpc__sk fpc__sk-logo-leg" />
          </div>
          <div className="fpc__sk fpc__sk-path" />
          <div className="fpc__sk fpc__sk-meta" />
        </div>
        <aside className="fpc__zone fpc__zone--price">
          <div className="fpc__sk fpc__sk-badge" />
          <div className="fpc__sk fpc__sk-price-lg" />
          <div className="fpc__sk fpc__sk-price-sm" />
          <div className="fpc__sk fpc__sk-summary" />
          <div className="fpc__sk fpc__sk-cta" />
          <div className="fpc__sk fpc__sk-trust" />
        </aside>
      </div>
    </div>
  );
}
